import { create } from 'zustand';
import { UserType, fetchManagerProfile, putManagerProfile } from './api/manager-api';
import { RolePermission } from './api/role-api';
import { useAlertStore } from './use-alert-store';
import { useAuthStore } from './use-auth-store';

export type PermissionServiceType = keyof UserType['permissions'];

interface ManagerStore {
  managerProfile?: Partial<UserType>;
  saveManagerProfile?: UserType;
  fetchManagerProfile: () => Promise<void>;
  isLoadingFetch?: boolean;
  handleUpdateManagerProfile: (data: Partial<UserType>) => void;
  handleSaveManagerProfile: () => Promise<void>;
  handleResetManagerProfile: () => void;
  hasAccess: (service: PermissionServiceType, permissions: RolePermission[]) => boolean;
  isLoading?: boolean;
}

export const useManagerStore = create<ManagerStore>((set, get) => ({
  fetchManagerProfile: async () => {
    set({ isLoadingFetch: true });
    try {
      const resp = await fetchManagerProfile();
      const changeData = useAuthStore.getState().changePasswordData;
      if (!changeData) {
        useAuthStore.getState().handleUpdateChangePasswordData({
          login: resp?.login,
          old_password: '',
          password: '',
          repeatPassword: '',
        });
      } else {
        useAuthStore.getState().handleUpdateChangePasswordData({ login: resp?.login });
      }
      set({ managerProfile: resp });
      set({ saveManagerProfile: resp });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по профилю менеджера', variant: 'danger' });
    } finally {
      set({ isLoadingFetch: false });
    }
  },
  handleUpdateManagerProfile: (data) => {
    set((store) => ({ ...store, managerProfile: { ...store.managerProfile, ...data } }));
  },
  handleResetManagerProfile: () => {
    set((store) => ({ ...store, managerProfile: store.saveManagerProfile }));
  },
  handleSaveManagerProfile: async () => {
    set({ isLoading: true });
    try {
      const data = get().managerProfile;
      if (!data) {
        return;
      }
      await putManagerProfile(data);
      useAlertStore.getState().add({ label: 'Информация успешно обновлена', variant: 'success' });
      set({ saveManagerProfile: data as UserType });
      set({ managerProfile: data });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось обновить данные по профилю менеджера', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },

  hasAccess: (service: PermissionServiceType, permissions: RolePermission[]) => {
    const { managerProfile } = get();

    const isEmptyPermissions = Object.keys(managerProfile?.permissions || {}).length === 0;

    if (
      isEmptyPermissions &&
      ['admin_head', 'admin_branch'].includes(managerProfile?.role as string)
    ) {
      return true;
    }

    return permissions.some((permission) => managerProfile?.permissions?.[service] === permission);
  },
}));
